import React from 'react';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../EmptyState';
import { SubscriptionsTierContentType } from '../../../../../types/subscriptionsSection';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { URLS } from '@wix/da-url';
import {
  ModalOptions,
  ModalType,
} from '@wix/da-shared-react/pkg/Modals/redux/types';

import s from './EmptyState.scss';

const ctaButtonNameByTierContentType = {
  [SubscriptionsTierContentType.DEVIATIONS]: 'submit_deviation',
  [SubscriptionsTierContentType.POSTS]: 'submit_post',
};

export type Props = {
  isOwner: boolean;
  profileOwnerUser: PapiUser;
  isDraft?: boolean;
  tier?: PapiDeviation;
  tierContentType: SubscriptionsTierContentType;
  showSubmitModal?: (type: string, options?: ModalOptions) => void;
  publishDraftTier?: (deviation: PapiDeviation) => void;
};

const TierContentEmptyState: React.FC<Props> = ({
  profileOwnerUser,
  isOwner,
  isDraft,
  tierContentType,
  tier,
  publishDraftTier,
  showSubmitModal,
}) => {
  const { t } = useTranslation();
  const { username } = profileOwnerUser;
  const canUserPublish = tier?.tier?.canUserPublish;

  const illustration = (
    <div className={s[isOwner ? 'owner-illustration' : 'illustration']} />
  );
  const { title, subtitle } = getTranslations({
    t,
    isOwner,
    isDraft,
    canUserPublish,
    username,
  });

  let ctaButton;
  if (isOwner && isDraft) {
    if (canUserPublish) {
      ctaButton = (
        <SolidButton
          className={s['cta']}
          size="medium"
          variant="approval-green"
          priority="primary"
          onClick={() => publishDraftTier && publishDraftTier(tier!)}
        >
          {t('widgets.tierContent.empty.cta.publish')}
        </SolidButton>
      );
    } else {
      ctaButton = (
        <BrandedButton
          variant="core-orange"
          size="large"
          href={URLS.CORE_MEMBERSHIP}
          biData={{
            evid: BiEventType.BUY_CORE_BUTTON,
            referral_info: ReferralType.SUBSCRIPTIONS_TIER_DRAFT,
          }}
        >
          {t('widgets.tierContent.empty.cta.getCore')}
        </BrandedButton>
      );
    }
  } else if (isOwner) {
    ctaButton = (
      <SolidButton
        className={s['cta']}
        size="medium"
        variant="approval-green"
        priority="primary"
        biData={{
          evid: BiEventType.TIER_ADD_CONTENT,
          typeid: ResourceType.TIER,
          itemid: tier?.deviationId,
          button_name: ctaButtonNameByTierContentType[tierContentType],
        }}
        onClick={() => {
          if (tierContentType === SubscriptionsTierContentType.DEVIATIONS) {
            // eslint-disable-next-line @wix/da/window-object-typeof-check
            window.location.assign(URLS.SUBMIT);
            return;
          }
          showSubmitModal && showSubmitModal(ModalType.STATUS_UPDATE);
        }}
      >
        {t(`widgets.tierContent.empty.cta.${tierContentType}`)}
      </SolidButton>
    );
  }

  return (
    <EmptyState
      className={s['empty-state']}
      illustration={illustration}
      title={title}
      subtitle={subtitle}
      customButton={ctaButton}
    />
  );
};

TierContentEmptyState.displayName = 'TierContentEmptyState';

export default TierContentEmptyState;

interface Translations {
  title: string;
  subtitle: string;
}

function getTranslations({
  t,
  isOwner,
  isDraft,
  canUserPublish,
  username,
}): Translations {
  let title;
  let subtitle;
  if (isOwner) {
    if (isDraft && !canUserPublish) {
      title = t('widgets.tierContent.empty.title.noCore');
      subtitle = t('widgets.tierContent.empty.subtitle.noCore');
    } else {
      title = isDraft
        ? t('widgets.tierContent.empty.title.owner.draft')
        : t('widgets.tierContent.empty.title.owner');
      subtitle = isDraft
        ? t('widgets.tierContent.empty.subtitle.owner.draft')
        : t('widgets.tierContent.empty.subtitle.owner');
    }
  } else {
    title = t('widgets.tierContent.empty.title.visitor');
    subtitle = t(`widgets.tierContent.empty.subtitle.visitor`, {
      username,
    });
  }
  return { title, subtitle };
}
